import { Component, OnDestroy, ViewChild, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ApiPaginationResult,
  ApiResult,
  PaginationResult,
} from '../../../../shared/models/api-result.model';
import { AssessmentRule } from '../../../models/assessment-rule.model';
import { AssessmentRuleService } from '../../../services/assessment-rule.service';
import { AssessmentService } from '../../../services/assessment.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Table, TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ToolbarModule } from 'primeng/toolbar';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { RouterLink } from '@angular/router';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { DropdownOption } from '../../../../shared/models/dropdowm.model';
import { PaginationService } from '../../../../shared/services/pagination.service';

@Component({
  selector: 'app-assessment-rule-list',
  imports: [
    FormsModule,
    InputTextModule,
    SelectModule,
    TableModule,
    BreadcrumbModule,
    ToolbarModule,
    ButtonModule,
    RouterLink,
    ProgressSpinnerModule,
    ToastModule,
    ToggleSwitchModule,
    ConfirmDialogModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './assessment-rule-list.component.html',
})
export class AssessmentRuleListComponent implements OnDestroy {
  @ViewChild('table') table?: Table;

  private assessmentRuleService = inject(AssessmentRuleService);
  private assessmentService = inject(AssessmentService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  paginationService = inject(PaginationService);

  assessmentRules = signal<AssessmentRule[]>([]);

  pageNumber = signal(1);
  pageSize = signal(10);
  totalRecords = signal(0);
  totalPages = signal(0);

  loading = signal(false);

  nameFilter?: string;
  assessmentIdFilter?: string;

  assessmentSelectLoading = signal(false);
  assessmentsDropdown = signal<DropdownOption[]>([]);

  private destroyFilter$ = new Subject<void>();
  private filterChanged$ = new Subject<void>();

  constructor() {
    this.filterChanged$
      .pipe(debounceTime(500), takeUntil(this.destroyFilter$))
      .subscribe(() => {
        this.pageNumber.set(0);
        this.table?.reset();
      });

    this.loadAssessmentsDropdown();
  }

  ngOnDestroy(): void {
    this.destroyFilter$.next();
    this.destroyFilter$.complete();
  }

  onFilterChange(): void {
    this.filterChanged$.next();
  }

  loadData(event: any): void {
    const page = this.paginationService.mapLazyEventToPageRequest(
      event,
      this.pageSize()
    );
    this.pageNumber.set(page.pageNumber);
    this.pageSize.set(page.pageSize);

    this.loading.set(true);

    this.assessmentRuleService
      .getAll(
        this.pageNumber(),
        this.pageSize(),
        this.nameFilter,
        this.assessmentIdFilter
      )
      .subscribe({
        next: (response: ApiPaginationResult) => {
          this.loading.set(false);

          const page = response.data as PaginationResult;
          this.assessmentRules.set(page.items as AssessmentRule[]);
          this.pageNumber.set(page.pageNumber);
          this.pageSize.set(page.pageSize);
          this.totalRecords.set(page.totalCount);
          this.totalPages.set(page.totalPages);
        },
        error: (e) => {
          this.loading.set(false);

          if (e instanceof HttpErrorResponse) {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao carregar as regras de avaliação',
              life: 3000,
            });
          }
        },
      });
  }

  requestUpdateStatus(item: AssessmentRule, event: any): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Você tem certeza que quer alterar o status da regra?',
      header: 'Confirmação',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancelar',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Alterar',
      },
      accept: () => {
        this.updateStatus(item);
      },
    });
  }

  private updateStatus(item: AssessmentRule): void {
    this.loading.set(true);

    this.assessmentRuleService.updateStatus(item.id, !item.isActive).subscribe({
      next: () => {
        this.loading.set(false);

        item.isActive = !item.isActive;

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Status atualizado com sucesso',
          life: 2000,
        });
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao atualizar o status',
            life: 3000,
          });
        }
      },
    });
  }

  private loadAssessmentsDropdown(): void {
    this.assessmentSelectLoading.set(true);

    this.assessmentService.getAllDropdown().subscribe({
      next: (response: ApiResult) => {
        this.assessmentSelectLoading.set(false);
        const options = response.data as DropdownOption[];
        this.assessmentsDropdown.set(options);
      },
      error: () => {
        this.assessmentSelectLoading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Falha',
          detail: 'Ocorreu um erro ao carregar avaliações',
          life: 3000,
        });
      },
    });
  }
}
